import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, Typography, FloatingLabelInput, Button, Illustration } from "@aviary";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./JourneyWaitlistModal.styles";

type WaitlistReason = "order_limit" | "store_not_ready" | "zipcode";

interface WaitlistContent {
  title: string;
  description: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (email: string, zipcode?: string) => void;
  prefilledEmail?: string;
  isLoading?: boolean;
  waitlistReason: WaitlistReason | null;
  dispensaryName?: string;
  /** Overrides the reason-derived copy (direct-lab checkout, BUT-1114). */
  content?: WaitlistContent;
  /**
   * The Journey flow captures the zipcode before this modal opens; flows that
   * don't (direct-lab checkout) collect it here since the waitlist requires it.
   */
  showZipCodeInput?: boolean;
}

const JourneyWaitlistModal = ({
  isOpen,
  onClose,
  onSubmit,
  prefilledEmail,
  isLoading,
  waitlistReason,
  dispensaryName,
  content,
  showZipCodeInput,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [email, setEmail] = useState(prefilledEmail || "");
  const [zipCode, setZipCode] = useState("");

  const isMobile = tablet.lessThan;

  const handleSubmit = () => {
    const trimmedEmail = email.trim();
    const trimmedZipCode = zipCode.trim();

    if (!trimmedEmail) return;
    if (showZipCodeInput && !trimmedZipCode) return;

    onSubmit?.(trimmedEmail, showZipCodeInput ? trimmedZipCode : undefined);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);

  // The direct-lab flow keeps this modal mounted and only toggles isOpen, so
  // the zip input — which has no prefill to re-sync from, unlike email above —
  // would otherwise keep a stale value across a close/reopen.
  useEffect(() => {
    if (!isOpen) {
      setZipCode("");
    }
  }, [isOpen]);

  const { title, description } =
    content ??
    {
      order_limit: {
        title: t(l.journeyCheckout.waitlist.titleOrderLimit),
        description: t(l.journeyCheckout.waitlist.descriptionOrderLimit),
      },
      store_not_ready: {
        title: t(l.journeyCheckout.waitlist.titleStoreNotReady),
        description: t(l.journeyCheckout.waitlist.descriptionStoreNotReady, { dispensaryName }),
      },
      zipcode: {
        title: t(l.journeyCheckout.waitlist.title),
        description: t(l.journeyCheckout.waitlist.description),
      },
    }[waitlistReason ?? "zipcode"];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <div css={styles.modalContainer}>
        <div css={styles.modalContent}>
          <div css={styles.iconSection}>
            <Illustration illustration="bell" />
          </div>

          <div css={styles.contentSection}>
            <div css={styles.textSection}>
              <Typography type="h4">{title}</Typography>
              <Typography type="body">{description}</Typography>
            </div>

            <div css={styles.formSection}>
              <FloatingLabelInput
                id="waitlist-email"
                label={t(l.journeyCheckout.waitlist.emailPlaceholder)}
                value={email}
                handleChange={e => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                type="email"
                autoComplete="email"
              />

              {!!showZipCodeInput && (
                <FloatingLabelInput
                  id="waitlist-zip-code"
                  label={t(l.journeyCheckout.availability.zipCodeLabel)}
                  value={zipCode}
                  handleChange={e => setZipCode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  type="text"
                  autoComplete="postal-code"
                />
              )}

              <div css={styles.buttonContainer}>
                <Button
                  onClick={handleSubmit}
                  intention="primary"
                  disabled={!email.trim() || (showZipCodeInput && !zipCode.trim()) || isLoading}
                  isLoading={isLoading}
                  isFullWidth={isMobile}
                  size={isMobile ? "large" : "medium"}
                >
                  {t(l.journeyCheckout.waitlist.submitButton)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default JourneyWaitlistModal;
export type { WaitlistReason };
